/**
 * Gemini API Client
 *
 * Handles communication with Cloud Functions backend for animation generation.
 * Features: timeout handling, retry with exponential backoff, response caching.
 */

import {
    GeminiAnimationRequest,
    GeminiAnimationResponse,
    LivePhotoError,
    AnimationType,
    AnimationIntensity,
} from './types';

/**
 * Configuration for the API client
 */
export interface GeminiApiClientConfig {
    /** Cloud Functions endpoint URL */
    endpoint: string;
    /** Request timeout in milliseconds (default: 10000) */
    timeout?: number;
    /** Maximum retry attempts (default: 3) */
    maxRetries?: number;
    /** Enable response caching (default: true) */
    enableCache?: boolean;
    /** Enable debug logging (default: false) */
    debug?: boolean;
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: Required<Omit<GeminiApiClientConfig, 'endpoint'>> = {
    timeout: 10000,
    maxRetries: 3,
    enableCache: true,
    debug: false,
};

/**
 * Cache key prefix for localStorage
 */
const CACHE_KEY_PREFIX = 'lmp_cache_';

/**
 * Cache TTL in milliseconds (30 days)
 */
const CACHE_TTL = 30 * 24 * 60 * 60 * 1000;

/**
 * Cached response structure
 */
interface CachedResponse {
    data: GeminiAnimationResponse;
    timestamp: number;
}

/**
 * GeminiApiClient class
 *
 * Handles fetching animation data from the Cloud Functions backend
 * with retry logic, timeout handling, and caching.
 */
export class GeminiApiClient {
    private config: Required<GeminiApiClientConfig>;

    constructor(config: GeminiApiClientConfig) {
        this.config = {
            ...DEFAULT_CONFIG,
            ...config,
        } as Required<GeminiApiClientConfig>;
    }

    /**
     * Fetch animation data for an image
     *
     * @param imageUrl - URL of the image to animate
     * @param options - Animation options
     * @returns Promise resolving to animation response
     */
    async fetch(
        imageUrl: string,
        options: {
            type?: AnimationType;
            intensity?: AnimationIntensity;
            loop?: boolean;
        } = {}
    ): Promise<GeminiAnimationResponse> {
        const imageHash = await this.computeHash(imageUrl);

        // Check cache first
        if (this.config.enableCache) {
            const cached = this.getFromCache(imageHash);
            if (cached) {
                if (this.config.debug) {
                    console.log('[GeminiApiClient] Cache hit for:', imageUrl);
                }
                return { ...cached, cacheStatus: 'hit' };
            }
        }

        // Prepare request
        const request: GeminiAnimationRequest = {
            imageUrl,
            imageHash,
            type: options.type ?? 'auto',
            intensity: options.intensity ?? 'subtle',
            loop: options.loop ?? true,
        };

        // Fetch with retry logic
        const response = await this.fetchWithRetry(request);

        // Cache successful response
        if (response.success && this.config.enableCache) {
            this.saveToCache(imageHash, response);
        }

        return { ...response, cacheStatus: 'miss' };
    }

    /**
     * Fetch with retry logic and exponential backoff
     */
    private async fetchWithRetry(
        request: GeminiAnimationRequest,
        attempt: number = 0
    ): Promise<GeminiAnimationResponse> {
        try {
            return await this.fetchWithTimeout(request);
        } catch (error) {
            const isRetryable =
                error instanceof LivePhotoError &&
                (error.code === 'NETWORK_ERROR' || error.code === 'TIMEOUT_ERROR');

            if (isRetryable && attempt < this.config.maxRetries) {
                // Exponential backoff: 1s, 2s, 4s, ...
                const delay = Math.pow(2, attempt) * 1000;
                if (this.config.debug) {
                    console.log(
                        `[GeminiApiClient] Retry ${attempt + 1}/${this.config.maxRetries} after ${delay}ms`
                    );
                }
                await this.sleep(delay);
                return this.fetchWithRetry(request, attempt + 1);
            }

            throw error;
        }
    }

    /**
     * Fetch with timeout handling
     */
    private async fetchWithTimeout(
        request: GeminiAnimationRequest
    ): Promise<GeminiAnimationResponse> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

        try {
            const response = await fetch(this.config.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(request),
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                if (response.status === 429) {
                    throw new LivePhotoError(
                        'Rate limit exceeded',
                        'RATE_LIMIT_ERROR'
                    );
                }
                throw new LivePhotoError(
                    `API error: ${response.status} ${response.statusText}`,
                    'API_ERROR'
                );
            }

            const data = (await response.json()) as GeminiAnimationResponse;
            return data;
        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof LivePhotoError) {
                throw error;
            }

            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new LivePhotoError(
                        `Request timed out after ${this.config.timeout}ms`,
                        'TIMEOUT_ERROR',
                        error
                    );
                }
                throw new LivePhotoError(
                    `Network error: ${error.message}`,
                    'NETWORK_ERROR',
                    error
                );
            }

            throw new LivePhotoError('Unknown error occurred', 'UNKNOWN_ERROR');
        }
    }

    /**
     * Compute SHA-256 hash of a string (for cache keys)
     */
    async computeHash(input: string): Promise<string> {
        // Use Web Crypto API if available
        if (typeof crypto !== 'undefined' && crypto.subtle) {
            const encoder = new TextEncoder();
            const data = encoder.encode(input);
            const hashBuffer = await crypto.subtle.digest('SHA-256', data);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        }

        // Fallback: simple hash for environments without crypto.subtle
        let hash = 0;
        for (let i = 0; i < input.length; i++) {
            const char = input.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(16);
    }

    /**
     * Get cached response if valid
     */
    private getFromCache(imageHash: string): GeminiAnimationResponse | null {
        if (typeof localStorage === 'undefined') {
            return null;
        }

        try {
            const cacheKey = CACHE_KEY_PREFIX + imageHash;
            const cached = localStorage.getItem(cacheKey);

            if (!cached) {
                return null;
            }

            const parsed: CachedResponse = JSON.parse(cached);

            // Check if cache is still valid
            if (Date.now() - parsed.timestamp > CACHE_TTL) {
                localStorage.removeItem(cacheKey);
                return null;
            }

            return parsed.data;
        } catch {
            return null;
        }
    }

    /**
     * Save response to cache
     */
    private saveToCache(imageHash: string, response: GeminiAnimationResponse): void {
        if (typeof localStorage === 'undefined') {
            return;
        }

        try {
            const cacheKey = CACHE_KEY_PREFIX + imageHash;
            const cached: CachedResponse = {
                data: response,
                timestamp: Date.now(),
            };
            localStorage.setItem(cacheKey, JSON.stringify(cached));
        } catch {
            // Ignore storage errors (quota exceeded, etc.)
            if (this.config.debug) {
                console.warn('[GeminiApiClient] Failed to cache response');
            }
        }
    }

    /**
     * Clear all cached responses
     */
    clearCache(): void {
        if (typeof localStorage === 'undefined') {
            return;
        }

        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    }

    /**
     * Sleep utility for retry delays
     */
    private sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
